/**
 * Message Input Component for dev-tools
 * Part of Phase 3 refactoring - Component Decomposition (UI Components)
 */
import React, { useMemo, useState } from "react";

import {
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { FieldSchema, FormState } from "../../types";
import {
  OneofValue,
  groupFieldsByOneof,
  validateField,
} from "../../utils/form-utils";
import { InputFactory } from "../input-factory";

interface MessageInputProps {
  field: FieldSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  touched?: boolean;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  field,
  value,
  onChange,
  error,
  touched,
}) => {
  const [showJsonEditor, setShowJsonEditor] = useState(false);
  const [nestedErrors, setNestedErrors] = useState<Record<string, string>>({});
  const [nestedTouched, setNestedTouched] = useState<Record<string, boolean>>(
    {},
  );
  const hasNestedFields = field.messageFields && field.messageFields.length > 0;
  const { plainFields, oneofGroups } = useMemo(
    () => groupFieldsByOneof(field.messageFields),
    [field.messageFields],
  );
  const oneofGroupNames = useMemo(
    () => Object.keys(oneofGroups),
    [oneofGroups],
  );

  const nestedFormState = useMemo((): FormState => {
    const currentValue =
      typeof value === "object" && value !== null
        ? (value as Record<string, unknown>)
        : {};
    return {
      data: currentValue,
      errors: nestedErrors,
      touched: nestedTouched,
      isValid: Object.keys(nestedErrors).length === 0,
      isSubmitting: false,
    };
  }, [value, nestedErrors, nestedTouched]);

  const handleNestedFieldChange = (fieldName: string, fieldValue: unknown) => {
    const currentValue =
      typeof value === "object" && value !== null
        ? (value as Record<string, unknown>)
        : {};
    onChange({ ...currentValue, [fieldName]: fieldValue });

    const nestedField = field.messageFields?.find((f) => f.name === fieldName);
    if (nestedField) {
      const errorMsg = validateField(nestedField, fieldValue);
      setNestedErrors((prev) => {
        if (!errorMsg) {
          const next = { ...prev };
          delete next[fieldName];
          return next;
        }
        return { ...prev, [fieldName]: errorMsg };
      });
    }
    setNestedTouched((prev) => ({ ...prev, [fieldName]: true }));
  };

  // Oneof members are mutually exclusive alternatives, not independent
  // fields — protobuf-es requires them wrapped as `{ case, value }` under
  // the oneof's own group name, not merged flat like handleNestedFieldChange
  // does. A flat `{ quota: {...} }` sibling key is silently dropped by the
  // message constructor instead of erroring, which is the bug this fixes.
  const handleOneofCaseChange = (groupName: string, newCase: string) => {
    const currentValue =
      typeof value === "object" && value !== null
        ? (value as Record<string, unknown>)
        : {};
    // Clear any error from the previous case's value — it doesn't apply to
    // the newly selected (empty) member and would otherwise show as stale.
    setNestedErrors((prev) => {
      const next = { ...prev };
      delete next[groupName];
      return next;
    });
    if (!newCase) {
      const rest = { ...currentValue };
      delete rest[groupName];
      onChange(rest);
      setNestedTouched((prev) => ({ ...prev, [groupName]: true }));
      return;
    }
    onChange({
      ...currentValue,
      [groupName]: { case: newCase, value: undefined },
    });
    setNestedTouched((prev) => ({ ...prev, [groupName]: true }));
  };

  const handleOneofValueChange =
    (groupName: string, caseName: string) =>
    (_fieldName: string, fieldValue: unknown) => {
      const currentValue =
        typeof value === "object" && value !== null
          ? (value as Record<string, unknown>)
          : {};
      onChange({
        ...currentValue,
        [groupName]: { case: caseName, value: fieldValue },
      });

      const members = oneofGroups[groupName];
      const selectedMember = members?.find((m) => m.name === caseName);
      if (selectedMember) {
        const errorMsg = validateField(selectedMember, fieldValue);
        setNestedErrors((prev) => {
          if (!errorMsg) {
            const next = { ...prev };
            delete next[groupName];
            return next;
          }
          return { ...prev, [groupName]: errorMsg };
        });
      }
      setNestedTouched((prev) => ({ ...prev, [groupName]: true }));
    };

  const handleJsonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Clear nested validation state so stale errors from the structured form
    // don't persist when the user switches back to it.
    setNestedErrors({});
    setNestedTouched({});
    try {
      const jsonValue = JSON.parse(e.target.value);
      onChange(jsonValue);
    } catch {
      // If not valid JSON, store as string
      onChange(e.target.value);
    }
  };

  const displayValue =
    typeof value === "object" && value !== null
      ? JSON.stringify(value, null, 2)
      : String(value || "{}");

  const renderJsonEditor = () => (
    <TextField
      multiline
      rows={3}
      fullWidth
      name={field.name}
      placeholder="Enter JSON object or leave empty for default"
      value={displayValue}
      onChange={handleJsonChange}
      error={!!error && touched}
      helperText={error && touched ? error : "Enter a valid JSON object"}
      size="small"
      variant="outlined"
      sx={{
        "& .MuiInputBase-input": {
          fontFamily: "Monaco, Consolas, 'Courier New', monospace",
          fontSize: "0.875rem",

          lineHeight: 1.5,
        },
        "& .MuiInputBase-root": {
          backgroundColor: "background.paper",
        },
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
        },
        "& .MuiFormHelperText-root": {
          margin: "4px 0 0 0",
        },
      }}
    />
  );

  const renderOneofGroup = (groupName: string) => {
    const members = oneofGroups[groupName];
    const oneofValue = nestedFormState.data[groupName] as
      OneofValue | undefined;
    const currentCase = oneofValue?.case ?? "";
    const selectedMember = members.find((m) => m.name === currentCase);

    return (
      <Box
        key={groupName}
        sx={{ display: "flex", flexDirection: "column", gap: 1 }}
      >
        <TextField
          select
          size="small"
          fullWidth
          variant="outlined"
          label={`${groupName} (choose one)`}
          value={currentCase}
          onChange={(e) => handleOneofCaseChange(groupName, e.target.value)}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {members.map((member) => (
            <MenuItem key={member.name} value={member.name}>
              {member.name}
            </MenuItem>
          ))}
        </TextField>
        {selectedMember && (
          <InputFactory
            field={selectedMember}
            value={oneofValue?.value}
            onChange={handleOneofValueChange(groupName, currentCase)}
            error={nestedFormState.errors[groupName]}
            touched={nestedFormState.touched[groupName]}
          />
        )}
      </Box>
    );
  };

  const renderNestedFields = () => {
    if (!hasNestedFields) return null;

    return (
      <Box
        sx={{
          backgroundColor: "background.paper",
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          p: 1.5,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {plainFields.map((nestedField) => (
            <Box key={nestedField.name}>
              <InputFactory
                field={nestedField}
                value={nestedFormState.data[nestedField.name]}
                onChange={handleNestedFieldChange}
                error={nestedFormState.errors[nestedField.name]}
                touched={nestedFormState.touched[nestedField.name]}
              />
            </Box>
          ))}
          {oneofGroupNames.map((groupName) => renderOneofGroup(groupName))}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: "600",
            color: "text.primary",
            fontSize: "0.9rem",
          }}
        >
          {field.name} (Message)
          {!field.isOptional && " *"}
        </Typography>
        {hasNestedFields && field.messageFields && (
          <Chip
            label={`${field.messageFields.length} fields`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              fontSize: "0.75rem",
              height: 20,
            }}
          />
        )}
      </Box>

      {field.description && (
        <Typography
          variant="caption"
          sx={{
            mb: 1.5,
            display: "block",
            color: "text.secondary",
            fontSize: "0.8rem",
            lineHeight: 1.4,
          }}
        >
          {field.description}
        </Typography>
      )}

      {hasNestedFields ? (
        <Box>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button
              size="small"
              variant={!showJsonEditor ? "contained" : "outlined"}
              onClick={() => setShowJsonEditor(false)}
              sx={{
                minWidth: 120,
                textTransform: "none",
                fontWeight: showJsonEditor ? "normal" : "medium",
              }}
            >
              Structured Form
            </Button>
            <Button
              size="small"
              variant={showJsonEditor ? "contained" : "outlined"}
              onClick={() => setShowJsonEditor(true)}
              sx={{
                minWidth: 120,
                textTransform: "none",
                fontWeight: !showJsonEditor ? "normal" : "medium",
              }}
            >
              JSON Editor
            </Button>
          </Stack>

          <Box>
            {showJsonEditor ? renderJsonEditor() : renderNestedFields()}
          </Box>
        </Box>
      ) : (
        renderJsonEditor()
      )}

      {error && touched && (
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 1, display: "block" }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};
